export interface BrowserInfo {
  browser: string;
  os: string;
  ip: string;
}

/**
 * Try to fetch the public IP using a lightweight external service.
 * Falls back to "Unknown" if network request fails or is unavailable.
 */
const fetchPublicIP = async (timeoutMs = 3000): Promise<string> => {
  if (typeof fetch === "undefined") return "Unknown";

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const res = await fetch("https://api.ipify.org?format=json", { signal: controller.signal });
    clearTimeout(timeout);
    if (!res.ok) return "Unknown";
    const data = await res.json();
    return data?.ip ?? "Unknown";
  } catch (e) {
    return "Unknown";
  }
};

/**
 * Detects the user's browser from the user agent string
 */
export const detectBrowser = (): string => {
  const userAgent = navigator.userAgent;
  
  if (userAgent.indexOf("Firefox") > -1) {
    return "Firefox";
  } else if (userAgent.indexOf("SamsungBrowser") > -1) {
    return "Samsung Internet";
  } else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) {
    return "Opera";
  } else if (userAgent.indexOf("Trident") > -1) {
    return "Internet Explorer";
  } else if (userAgent.indexOf("Edge") > -1 || userAgent.indexOf("Edg") > -1) {
    return "Edge";
  } else if (userAgent.indexOf("Chrome") > -1) {
    return "Chrome";
  } else if (userAgent.indexOf("Safari") > -1) {
    return "Safari";
  } else {
    return "Unknown";
  }
};

/**
 * Detects the user's operating system from the user agent string
 */
export const detectOS = (): string => {
  const userAgent = navigator.userAgent;
  const platform = navigator.platform;
  
  if (userAgent.indexOf("Win") > -1) {
    return "Windows";
  } else if (userAgent.indexOf("Mac") > -1) {
    return "MacOS";
  } else if (userAgent.indexOf("X11") > -1 || userAgent.indexOf("Linux") > -1) {
    return "Linux";
  } else if (userAgent.indexOf("Android") > -1) {
    return "Android";
  } else if (userAgent.indexOf("like Mac") > -1 || /iPad|iPhone|iPod/.test(platform)) {
    return "iOS";
  } else {
    return "Unknown";
  }
};

/**
 * Gathers all browser information including IP address
 */
export const getBrowserInfo = async (): Promise<BrowserInfo> => {
  const browser = detectBrowser();
  const os = detectOS();
  const ip = await fetchPublicIP();
  
  return {
    browser,
    os,
    ip,
  };
};
